import parseMilliseconds from 'parse-ms';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EVENTS, IdleTimerAPI, useIdleTimer } from 'react-idle-timer';
import { toast } from 'react-toastify';

import { useAuth } from './AuthorizationProvider';
import { useConfig } from './ConfigurationProvider';
// eslint-disable-next-line import-x/no-cycle
import { sessionStateMock } from '../testUtils/sessionProviderMock';
import { timePartsToHumanReadableString } from '../utils/countDownUtils';

export interface SessionInformation extends IdleTimerAPI {
  setCustomSessionIdleTime: (sessionIdleInMs: number) => void; // for overriding session timeout
}

const NON_IDLE_EVENTS: EVENTS[] = ['keydown', 'mousedown', 'touchstart'];
const SESSION_EXPIRATION_TOAST_ID = 'SESSION_EXPIRATION_TOAST_ID';

export const SessionProviderContext = React.createContext(sessionStateMock);

/**
 * Provides session expiration functionality to rest of the application. It has to be used within auth context in order
 * to extract necessary information about session idle time (`sessionIdleTimeDuration`). This parameter is computed
 * inside AuthorizationProvider check {@link getSessionTimeoutFromRefreshToken} for implementation details.
 *
 * In special cases this value can be overridden by `setCustomSessionIdleTime` function received from this hook.
 *
 * User is notified about session expiration prior to logging out by toast notification. Notification duration is set by
 * default to 3 minutes before logging out but when session expiration time is shorter than notification duration than
 * 80% of expiration time is used. Expiration time can be modified by editing `SSO Session Idle` parameter in Keycloak
 * admin interface under Realm Settings > Tokens.
 * Notification time can be configured in `.env` file of your environment under `REACT_APP_NOTIFICATION_DURATION` key.
 */
export const SessionProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const authContext = useAuth();
  if (!authContext) {
    throw new Error('SessionProvider has to be used inside auth context!');
  }

  const { notificationDuration } = useConfig();
  const { sessionIdleTimeDuration, logout } = authContext;

  const [customSessionIdle, setCustomSessionIdle] = useState<number | undefined>(undefined);
  const [notificationRegistration, setNotificationRegistration] = useState<NodeJS.Timeout | undefined>(undefined);
  const [countDownRegistration, setCountDownRegistration] = useState<NodeJS.Timeout | undefined>(undefined);
  const [notificationRemainingTime, setNotificationRemainingTime] = useState(0);

  const { t } = useTranslation();

  /**
   * @param {number} time - custom session timeout in millis
   */
  const setCustomSessionIdleTimeFromConsumer = (time: number) => {
    if (time == null || Number.isNaN(time) || time <= 0) {
      console.warn('Custom session timeout was not applied, incorrect value was received!');
      return;
    }
    setCustomSessionIdle(time);
  };

  const logoutIdleUser = () => {
    console.debug(`User is being logged out due to idle timeout.`);
    console.debug(`Session idle time is set to ${customSessionIdle || sessionIdleTimeDuration}`);

    setNotificationRemainingTime(0);

    setNotificationRegistration((prevNotification) => {
      if (prevNotification) {
        clearTimeout(prevNotification);
      }

      return undefined;
    });

    setCountDownRegistration((prevCountDown) => {
      if (prevCountDown) {
        clearTimeout(prevCountDown);
      }

      return undefined;
    });
    logout();
  };

  const getToastNotificationDuration = useCallback(() => {
    if (sessionIdleTimeDuration > notificationDuration) {
      return notificationDuration;
    }

    return Math.ceil(Math.round(sessionIdleTimeDuration * 0.8) / 10000) * 10000; // 10s in millis here
  }, [notificationDuration, sessionIdleTimeDuration]);

  const getToastNotificationStartTime = useCallback(
    (notificationDurationInMillis: number) => {
      return sessionIdleTimeDuration - notificationDurationInMillis;
    },
    [sessionIdleTimeDuration]
  );

  const createToastContent = (duration: number) => {
    const timeCaption = timePartsToHumanReadableString(parseMilliseconds(duration), t);
    return (
      <div>
        {t('session.notifyLogout')}
        <p>{timeCaption}</p>
      </div>
    );
  };

  const registerNotification = useCallback(() => {
    const toastNotificationDuration = getToastNotificationDuration();

    return setTimeout(() => {
      if (!toast.isActive(SESSION_EXPIRATION_TOAST_ID)) {
        toast.warning(createToastContent(toastNotificationDuration), {
          autoClose: toastNotificationDuration,
          toastId: SESSION_EXPIRATION_TOAST_ID,
          progressClassName: 'Toastify__progress-bar--animated',
        });
      }
      setNotificationRemainingTime(toastNotificationDuration);
    }, getToastNotificationStartTime(toastNotificationDuration));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getToastNotificationDuration, getToastNotificationStartTime]);

  const refreshToastRegistrations = () => {
    toast.dismiss(SESSION_EXPIRATION_TOAST_ID);

    if (countDownRegistration) {
      clearTimeout(countDownRegistration);
    }
    setNotificationRemainingTime(0);

    setNotificationRegistration((prevNotification) => {
      if (prevNotification) {
        clearTimeout(prevNotification);
      }

      return registerNotification();
    });
  };

  const idleTimer = useIdleTimer({
    timeout: customSessionIdle || sessionIdleTimeDuration, // IdleTimer is expecting millis here
    events: NON_IDLE_EVENTS,
    onAction: refreshToastRegistrations,
    onIdle: logoutIdleUser,
    throttle: 1000, // refresh toast registrations only once a second
  });

  useEffect(() => {
    if (notificationRemainingTime <= 0) {
      return;
    }

    const toastNotificationDuration = getToastNotificationDuration();
    const remainingTimePrecise = idleTimer.getRemainingTime();
    const remainingTimePreciseToDisplay = Math.round(remainingTimePrecise / 1000) * 1000;

    toast.update(SESSION_EXPIRATION_TOAST_ID, {
      render: createToastContent(remainingTimePreciseToDisplay),
      progress: remainingTimePrecise / toastNotificationDuration,
    });

    setCountDownRegistration(
      setTimeout(() => {
        setNotificationRemainingTime(remainingTimePrecise - 1000);
      }, 1000)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationRemainingTime]);

  useEffect(() => {
    return () => {
      if (notificationRegistration) {
        clearTimeout(notificationRegistration);
      }
    };
  }, [notificationRegistration]);

  useEffect(() => {
    return () => {
      if (countDownRegistration) {
        clearTimeout(countDownRegistration);
      }
    };
  }, [countDownRegistration]);

  useEffect(() => {
    refreshToastRegistrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SessionProviderContext.Provider
      value={{ setCustomSessionIdleTime: setCustomSessionIdleTimeFromConsumer, ...idleTimer }}
    >
      {children}
    </SessionProviderContext.Provider>
  );
};

export const SessionProviderConsumer = SessionProviderContext.Provider;

export const useSession = () => React.useContext(SessionProviderContext);
